import { useMemo } from "react";

import {
  useAllAttempts,
  Attempt,
  Project,
  Revision,
  useProjects,
} from "_/data/projects";
import { useMachines, Machine } from "_/data/machines";
import { User } from "_/data/users";
import {
  useRemovedPrintQueueEntries,
  PrintQueueEntry,
} from "_/data/print-queue";

import { Uuid } from "_/types";
import { indexById } from "_/utils";

// Enriched row type combining attempt data with related entities for display
export type JobRow = {
  attempt: Attempt;
  projectId?: Uuid;
  projectName?: string;
  revisionId: Uuid;
  revisionNumber?: number;
  machine?: Machine;
  submittedBy?: User;
  submittedAt?: string;
};

// Filter out cancelled jobs shorter than this (calibration jobs)
const MIN_CANCELLED_JOB_MINUTES = 5;

// Default history limit for widgets/tables
const DEFAULT_HISTORY_LIMIT = 30;

// Sort functions for attempts
const sortByStartedAtDesc = (a: Attempt, b: Attempt) =>
  Date.parse(b.startedAt) - Date.parse(a.startedAt);

const sortByEndedAtDesc = (a: Attempt, b: Attempt) => {
  const aTime = a.endedAt || a.startedAt;
  const bTime = b.endedAt || b.startedAt;
  return Date.parse(bTime) - Date.parse(aTime);
};

/**
 * Fetches and enriches job data for the Jobs page.
 *
 * This hook performs client-side enrichment by fetching data from multiple
 * endpoints and joining them together. Queue entries are fetched with user
 * includes for submission info. Returns two arrays: printing jobs and history jobs.
 *
 * Slices before enriching for performance - only enriches displayed jobs.
 */
export function useJobsData({
  historyLimit = DEFAULT_HISTORY_LIMIT,
}: {
  historyLimit?: number;
} = {}) {
  const { data: allAttempts = [], isLoading: attemptsLoading } =
    useAllAttempts();

  // Fetch removed queue entries with user info (for submission info).
  // Queue entries are marked as "removed" as soon as printing starts.
  // Only fetch the 50 most recent removed queue entries for performance.
  const { data: removedEntries = [] } = useRemovedPrintQueueEntries({
    limit: 50,
    include: ["users"],
  });

  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: machines = [], isLoading: machinesLoading } = useMachines();

  // Filter and split attempts
  const { printingAttempts, historyAttempts } = useMemo(() => {
    // Filter out short cancelled jobs (calibration jobs)
    const filteredAttempts = allAttempts.filter(
      (attempt) =>
        !(
          attempt.status === "cancelled" &&
          attempt.endedAt &&
          new Date(attempt.endedAt).getTime() -
            new Date(attempt.startedAt).getTime() <
            MIN_CANCELLED_JOB_MINUTES * 60 * 1000
        )
    );

    const printingAttemptsInner: Attempt[] = [];
    const historyAttemptsInner: Attempt[] = [];

    for (const attempt of filteredAttempts) {
      if (attempt.status === "printing" || attempt.status === "paused") {
        printingAttemptsInner.push(attempt);
      } else {
        historyAttemptsInner.push(attempt);
      }
    }

    printingAttemptsInner.sort(sortByStartedAtDesc);
    historyAttemptsInner.sort(sortByEndedAtDesc);

    return {
      printingAttempts: printingAttemptsInner,
      historyAttempts: historyAttemptsInner,
    };
  }, [allAttempts]);

  const lookupMaps = useMemo(() => {
    const machineById = indexById(machines);

    const projectRevisionsById = new Map<
      Uuid,
      { project: Project; revision: Revision }
    >();
    projects.forEach((project) => {
      project.revisions.forEach((revision) => {
        projectRevisionsById.set(revision.id, { project, revision });
      });
    });

    // Build attemptId to queueEntry map from removed entries
    const attemptToQueueEntry = new Map<Uuid, PrintQueueEntry>();
    for (const entry of removedEntries) {
      for (const attempt of entry.attempts) {
        attemptToQueueEntry.set(attempt.id, entry);
      }
    }

    return { machineById, projectRevisionsById, attemptToQueueEntry };
  }, [projects, machines, removedEntries]);

  const { printingRows, historyRows } = useMemo(() => {
    const { machineById, projectRevisionsById, attemptToQueueEntry } =
      lookupMaps;

    function enrichAttempt(attempt: Attempt): JobRow {
      const revisionMeta = projectRevisionsById.get(attempt.revisionId);
      const queueEntry = attemptToQueueEntry.get(attempt.id);
      return {
        attempt,
        projectId: revisionMeta?.project.id,
        projectName: revisionMeta?.project.name,
        revisionId: attempt.revisionId,
        revisionNumber: revisionMeta?.revision.number,
        machine: machineById[attempt.machineId],
        submittedBy: queueEntry?.submittedByUser,
        submittedAt: queueEntry?.createdAt,
      };
    }

    const printingRowsInner = printingAttempts.map(enrichAttempt);
    const historyRowsInner = historyAttempts
      .slice(0, historyLimit)
      .map(enrichAttempt);

    return { printingRows: printingRowsInner, historyRows: historyRowsInner };
  }, [printingAttempts, historyAttempts, historyLimit, lookupMaps]);

  const isLoading = attemptsLoading || projectsLoading || machinesLoading;

  return { printingRows, historyRows, isLoading };
}
